/**
 * onboarding-mobile.tsx — Trang "Bắt đầu" rút gọn cho ĐIỆN THOẠI (< 768px).
 *
 * Bản máy tính dẫn người dùng tới các trang chỉ dùng được trên máy tính (kết nối kênh, AI, tự động hoá, mời đồng nghiệp — xem
 * lib/mobile-scope.ts), nên trên điện thoại mỗi nút sẽ rơi vào thẻ "chức năng dùng trên máy tính". Bản này giữ việc LÀM ĐƯỢC
 * trên điện thoại (trả lời tin đầu tiên, theo dõi tiến độ) và biến phần cần máy tính thành "Gửi liên kết sang máy tính".
 */
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { ArrowRight, Bot, Check, ChevronRight, Headset, Lock, MessagesSquare, MonitorSmartphone, PlugZap, RefreshCw, Send, UserPlus, Workflow, type LucideIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { SHARE_TOAST, absoluteUrl, shareLink } from '@/lib/share-link'
import { ONBOARDING_LINKS, isExternalUrl, progressPercent, withOnboardingOrigin, type OnboardingSteps, type OnboardingStepNo, type QuickActionAccess } from '@/lib/onboarding'

interface Props {
  steps: OnboardingSteps
  access: QuickActionAccess
  canInbox: boolean
  channelsConnected: number
  refreshing: boolean
  skipping: boolean
  onRefresh: () => void
  onSkip: () => void
  onOpenStep: (n: OnboardingStepNo) => void
}

async function sendToDesktop(path: string, title: string) {
  const r = SHARE_TOAST[await shareLink(absoluteUrl(path, window.location.origin), title)]
  if (r.type) toast[r.type](r.text)
}

export function OnboardingMobile({ steps, access, canInbox, channelsConnected, refreshing, skipping, onRefresh, onSkip, onOpenStep }: Props) {
  const { t } = useTranslation('growth')
  const advisor = ONBOARDING_LINKS.productAdvisor
  const connectPath = withOnboardingOrigin('/integrations', 1)
  const desktopTasks: { step: OnboardingStepNo; icon: LucideIcon; title: string; text: string; path: string; key: keyof QuickActionAccess }[] = [
    { step: 3, icon: Bot, title: t('onboardingMobile.task.ai.title'), text: t('onboardingMobile.task.ai.text'), path: '/ai', key: 'ai' },
    { step: 4, icon: Workflow, title: t('onboardingMobile.task.automation.title'), text: t('onboardingMobile.task.automation.text'), path: '/automation', key: 'automation' },
    { step: 5, icon: UserPlus, title: t('onboardingMobile.task.invite.title'), text: t('onboardingMobile.task.invite.text'), path: '/team/members?add=human', key: 'invite' },
  ]
  return (
    <div className="space-y-4 pb-4">
      <section className="rounded-2xl bg-gradient-to-br from-primary/10 via-primary/5 to-transparent p-4">
        <h1 className="text-lg font-semibold leading-snug">{t('onboardingMobile.heroTitle')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t('onboardingMobile.heroDesc')}</p>
        <div className="mt-3 flex items-center gap-3">
          <span className="text-sm font-medium tabular-nums">{steps.doneCount}/{steps.total}</span>
          <div
            className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted"
            role="progressbar"
            aria-label={t('onboardingMobile.progressLabel')}
            aria-valuemin={0}
            aria-valuemax={steps.total}
            aria-valuenow={steps.doneCount}
          >
            <div className="h-full rounded-full bg-gradient-to-r from-primary to-[#4f7bff] transition-[width] duration-500" style={{ width: `${progressPercent(steps)}%` }} />
          </div>
          <button type="button" onClick={onRefresh} disabled={refreshing} aria-label={t('onboardingMobile.refreshLabel')} className="text-primary disabled:opacity-60">
            <RefreshCw className={cn('h-4 w-4', refreshing && 'animate-spin')} />
          </button>
        </div>
      </section>

      <ol className="space-y-3">
        <StepCard
          icon={PlugZap}
          state={steps.connect}
          title={t('onboardingMobile.step.connect.title')}
          text={steps.connect === 'done' ? t('onboardingMobile.step.connect.textDone', { count: channelsConnected }) : t('onboardingMobile.step.connect.textPending')}
        >
          {access.connect && (
            <Button variant={steps.connect === 'done' ? 'outline' : 'default'} className="h-10 w-full" onClick={() => void sendToDesktop(connectPath, t('onboardingMobile.connectShareTitle'))}>
              <MonitorSmartphone /> {steps.connect === 'done' ? t('onboardingMobile.sendManageLink') : t('onboardingMobile.sendDesktopLink')}
            </Button>
          )}
        </StepCard>

        <StepCard
          icon={MessagesSquare}
          state={steps.reply}
          title={t('onboardingMobile.step.reply.title')}
          text={t('onboardingMobile.step.reply.text')}
        >
          {steps.reply === 'locked' ? (
            <Button variant="secondary" className="h-10 w-full" disabled><Lock /> {t('onboardingMobile.waitingConnect')}</Button>
          ) : canInbox ? (
            <Button asChild variant={steps.reply === 'done' ? 'outline' : 'default'} className="h-10 w-full">
              <Link to={withOnboardingOrigin('/conversations', 2)} onClick={() => onOpenStep(2)}>{t('onboardingMobile.openInbox')} <ArrowRight /></Link>
            </Button>
          ) : null}
        </StepCard>
      </ol>

      {desktopTasks.some((task) => access[task.key]) && (
        <section>
          <h2 className="text-sm font-semibold">{t('onboardingMobile.continueOnDesktop')}</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">{t('onboardingMobile.continueOnDesktopDesc')}</p>
          <ul className="mt-2 divide-y overflow-hidden rounded-xl border bg-card">
            {desktopTasks.filter((task) => access[task.key]).map((task) => (
              <li key={task.step}>
                <button
                  type="button"
                  onClick={() => void sendToDesktop(withOnboardingOrigin(task.path, task.step), task.title)}
                  className="flex w-full items-center gap-3 px-3 py-3 text-left active:bg-muted"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary"><task.icon className="h-5 w-5" /></span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium leading-tight">{task.title}</span>
                    <span className="mt-0.5 block text-xs leading-snug text-muted-foreground">{task.text}</span>
                  </span>
                  <Send className="h-4 w-4 shrink-0 text-muted-foreground" aria-label={t('onboardingMobile.sendDesktopLink')} />
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="space-y-1 rounded-xl border bg-card p-1">
        {advisor ? (
          isExternalUrl(advisor)
            ? <a href={advisor} target="_blank" rel="noopener noreferrer" className={HELP}><Headset className="h-4 w-4 text-muted-foreground" /><span className="flex-1">{t('onboardingMobile.contactAdvisor')}</span><ChevronRight className="h-4 w-4 text-muted-foreground" /></a>
            : <Link to={advisor} className={HELP}><Headset className="h-4 w-4 text-muted-foreground" /><span className="flex-1">{t('onboardingMobile.contactAdvisor')}</span><ChevronRight className="h-4 w-4 text-muted-foreground" /></Link>
        ) : null}
        <button type="button" onClick={onSkip} disabled={skipping} className={cn(HELP, 'disabled:opacity-50')}>
          <ChevronRight className="h-4 w-4 text-muted-foreground" /><span className="flex-1">{t('onboardingMobile.skipToSystem')}</span>
        </button>
      </div>
    </div>
  )
}

const HELP = 'flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left text-sm active:bg-muted'

function StepCard({ icon: Icon, state, title, text, children }: { icon: LucideIcon; state: 'done' | 'current' | 'locked'; title: string; text: string; children?: React.ReactNode }) {
  return (
    <li className={cn('rounded-2xl border bg-card p-4', state === 'current' && 'border-primary/40 shadow-sm', state === 'locked' && 'opacity-70')}>
      <div className="flex items-start gap-3">
        <span className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-xl', state === 'done' ? 'bg-emerald-500/15 text-emerald-600' : 'bg-primary/10 text-primary')}>
          {state === 'done' ? <Check className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold leading-tight">{title}</p>
          <p className="mt-1 text-xs leading-snug text-muted-foreground">{text}</p>
        </div>
      </div>
      {children && <div className="mt-3">{children}</div>}
    </li>
  )
}
